import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Receipt, LinkedReceipt } from 'src/services/receipts'
import { AiGeneratedExpense } from 'src/services/receipts/api'
import { receiptApi } from 'src/services/receipts/api'
import { receiptQueries } from 'src/services/receipts/queries'
import { Button } from 'src/components/button'
import { Modal } from 'src/components/modal'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { FaCheck } from 'react-icons/fa'
import {
  EyeIcon,
  TrashIcon,
  SparklesIcon,
  DocumentDuplicateIcon,
  LinkSlashIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import { cn } from 'src/utils/style'
import { ReceiptListItemActionWrapper } from './receipt-list-item-action-wrapper'
import { ReceiptNotesModal } from './receipt-notes-modal'
import { useLongPress } from 'src/hooks/use-long-press'
import { Gate } from 'src/components/gate'
import { useIsJobActive, useRecentJob, useAsyncJobStore } from 'src/stores/async-jobs'
import { taskTypeEnum, buildOptimisticJob } from 'src/services/async-jobs/models'
import { useOrgFeatureConfig } from 'src/hooks/use-org-feature-config'
import { OrgPicker } from 'src/components/org-picker'
import { useEligibleBillingOrgs, RECEIPT_BILLING } from 'src/hooks/use-eligible-billing-orgs'
import { useToast } from 'src/components/toast'
import { extractErrorMessage } from 'src/utils/errors'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { format } from 'date-fns'

interface ReceiptListPageActionWrapperProps {
  receipt: Receipt
  onView: (receipt: Receipt | LinkedReceipt) => void
  onClick?: (receipt: Receipt, event: React.MouseEvent) => void
  onUnlink?: (receiptId: string, linkedReceiptId: string) => void
  isSelected?: boolean
  isUnlinking?: boolean
  viewMode?: ViewMode
}

export const ReceiptListPageActionWrapper: React.FC<ReceiptListPageActionWrapperProps> = ({
  receipt,
  onView,
  onClick,
  onUnlink,
  isSelected = false,
  isUnlinking = false,
  viewMode = 'card',
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false)
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [context, setContext] = useState('')
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [carouselIndex, setCarouselIndex] = useState(0)

  // AI generation spends wallet credits, so the user must pick which org pays.
  const { eligibleMemberships, canGenerate } = useEligibleBillingOrgs(RECEIPT_BILLING)

  // Create array of all receipts in the stack (primary + linked)
  // LinkedReceipt has id, name, asset - sufficient for display
  const allReceiptsInStack = useMemo((): (Receipt | LinkedReceipt)[] => {
    const receipts: (Receipt | LinkedReceipt)[] = [receipt]
    if (receipt.linkedReceiptsRef) {
      receipts.push(...receipt.linkedReceiptsRef)
    }
    return receipts
  }, [receipt])

  const hasMultipleReceipts = allReceiptsInStack.length > 1
  const currentDisplayReceipt = allReceiptsInStack[carouselIndex] || receipt

  const { data: featureConfig } = useOrgFeatureConfig()
  const aiReceiptScanningEnabled = featureConfig?.aiReceiptScanningEnabled !== false
  const isAnalysisActive = useIsJobActive(taskTypeEnum.RECEIPT_ANALYSIS, currentDisplayReceipt.id)
  const recentAnalysisJob = useRecentJob(taskTypeEnum.RECEIPT_ANALYSIS, currentDisplayReceipt.id)
  const completedJobResult =
    recentAnalysisJob?.status === 'done' ? (recentAnalysisJob.result as AiGeneratedExpense) : null
  const restoreActive = useAsyncJobStore((s) => s.actions.restoreActive)

  // Reset carousel index when linked receipts change
  useEffect(() => {
    if (carouselIndex >= allReceiptsInStack.length) {
      setCarouselIndex(0)
    }
  }, [allReceiptsInStack.length, carouselIndex])

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCarouselIndex((prev) => (prev === 0 ? allReceiptsInStack.length - 1 : prev - 1))
  }

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCarouselIndex((prev) => (prev === allReceiptsInStack.length - 1 ? 0 : prev + 1))
  }

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: receiptApi.remove,
    queryKey: receiptQueries.all(),
  })

  const { mutate: generateCustom, isPending: isGenerating } = useMutation({
    mutationFn: ({
      receiptId,
      organization,
      context,
    }: {
      receiptId: string
      organization: string
      context?: string
    }) => receiptApi.csc.generateCustom({ receiptId, organization, context: context || undefined }),
    onSuccess: (data, variables) => {
      setIsGenerateModalOpen(false)
      setContext('')
      // Drop the job into the active store immediately so the spinner is on the
      // instant the dispatch returns — no wait for the first safety poll. The ws
      // terminal event (or the poll) supersedes this same-id entry on completion.
      restoreActive(
        buildOptimisticJob({
          id: data.asyncJobId,
          taskType: taskTypeEnum.RECEIPT_ANALYSIS,
          entityId: variables.receiptId,
          entityType: 'Receipt',
          label: t('asyncJobs.analyzingReceipt'),
        }),
      )
    },
    onError: (error) => {
      setIsGenerateModalOpen(false)
      toast({
        description: extractErrorMessage(error, t('receipts.generateError')),
        type: 'error',
      })
    },
  })

  const isAnalyzing = isGenerating || isAnalysisActive

  const { mutate: duplicateReceipt, isPending: isDuplicating } = useMutation({
    mutationFn: (receiptId: string) => receiptApi.csc.duplicate({ receiptId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: receiptQueries.all() })
    },
    onError: (error) => {
      console.error('Failed to duplicate receipt:', error)
    },
  })

  const handleDelete = () => {
    requestDelete(currentDisplayReceipt.id, currentDisplayReceipt.name)
  }

  const handleOpenGenerateModal = () => {
    // Force an explicit pick when several orgs qualify; auto-select the only
    // one when there's no ambiguity.
    setSelectedOrgId(eligibleMemberships.length === 1 ? eligibleMemberships[0].organizationId : '')
    setIsGenerateModalOpen(true)
  }

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false)
    setContext('')
    setSelectedOrgId('')
  }

  const handleGenerateWithContext = () => {
    if (!selectedOrgId) return
    generateCustom({
      receiptId: currentDisplayReceipt.id,
      organization: selectedOrgId,
      context: context.trim() || undefined,
    })
  }

  const handleDuplicate = () => {
    duplicateReceipt(currentDisplayReceipt.id)
  }

  const handleLongPress = useCallback(() => {
    onClick?.(receipt, {
      metaKey: true,
      ctrlKey: false,
      preventDefault: () => {},
    } as React.MouseEvent)
  }, [onClick, receipt])

  const { handlers: longPressHandlers, didLongPressRef } = useLongPress({
    onLongPress: handleLongPress,
  })

  const isListView = viewMode === 'list'

  const prevButton = (
    <Button
      onClick={handlePrevious}
      variant="secondary"
      iconOnly
      shape="circle"
      size="sm"
      className={cn('shadow-md', !isListView && 'absolute left-1 top-1/2 z-20 -translate-y-1/2')}
      title={t('receipts.previousReceipt')}
      data-testid="carousel-prev"
    >
      <ChevronLeftIcon className="h-3 w-3 text-gray-700" />
    </Button>
  )

  const nextButton = (
    <Button
      onClick={handleNext}
      variant="secondary"
      iconOnly
      shape="circle"
      size="sm"
      className={cn('shadow-md', !isListView && 'absolute right-1 top-1/2 z-20 -translate-y-1/2')}
      title={t('receipts.nextReceipt')}
      data-testid="carousel-next"
    >
      <ChevronRightIcon className="h-3 w-3 text-gray-700" />
    </Button>
  )

  const createdLabel =
    'created' in currentDisplayReceipt && currentDisplayReceipt.created
      ? format(new Date(currentDisplayReceipt.created), 'yyyy-MM-dd')
      : null
  const notes = 'notes' in currentDisplayReceipt ? currentDisplayReceipt.notes : null

  const listMeta = isListView ? (
    <>
      {createdLabel && <span className="whitespace-nowrap">{createdLabel}</span>}
      {hasMultipleReceipts && (
        <span
          className="flex items-center gap-1 whitespace-nowrap"
          data-testid="receipt-linked-state"
        >
          {prevButton}
          <span>
            {t('common.linked')} {carouselIndex + 1}/{allReceiptsInStack.length}
          </span>
          {nextButton}
        </span>
      )}
      {isAnalyzing ? (
        <span className="whitespace-nowrap text-accent" data-testid="receipt-analysis-state">
          {t('asyncJobs.analyzingReceipt')}
        </span>
      ) : completedJobResult ? (
        <span className="whitespace-nowrap text-green" data-testid="receipt-analysis-state">
          {t('receipts.analysisReady')}
        </span>
      ) : null}
      {notes && (
        <span className="min-w-0 truncate" title={notes}>
          {notes}
        </span>
      )}
    </>
  ) : undefined

  return (
    <>
      <ConfirmDeleteDialog {...dialogProps} />
      <ReceiptNotesModal
        receiptId={currentDisplayReceipt.id}
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
      />
      <Modal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        title={t('receipts.generateExpense')}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">
              {t('billing.selectOrganization')}
            </label>
            <OrgPicker
              memberships={eligibleMemberships}
              value={selectedOrgId || null}
              onChange={setSelectedOrgId}
            />
            <p className="mt-1 text-xs text-text-tertiary">{t('receipts.generationBilledTo')}</p>
          </div>
          <p className="text-sm text-text-secondary">{t('receipts.addContext')}</p>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t('receipts.contextPlaceholder')}
            className="w-full rounded-md border border-border-strong p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            rows={3}
            data-testid="generate-context-input"
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={handleCloseGenerateModal} disabled={isGenerating}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleGenerateWithContext}
              disabled={isGenerating || !selectedOrgId}
              data-testid="generate-submit-button"
            >
              {isGenerating ? t('common.generating') : t('receipts.generate')}
            </Button>
          </div>
        </div>
      </Modal>
      <div
        onClick={(e) => {
          if (didLongPressRef.current) {
            didLongPressRef.current = false
            return
          }
          onClick?.(receipt, e)
        }}
        {...longPressHandlers}
        className={cn(
          'relative cursor-pointer rounded-xl transition-all',
          isSelected && 'bg-gray-100 ring-2 ring-primary',
        )}
      >
        {/* Selection checkmark badge */}
        {isSelected && (
          <div className="absolute -right-2 -top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white shadow-md">
            <FaCheck className="h-3 w-3" />
          </div>
        )}

        {/* Linked receipts badge with carousel indicator */}
        {hasMultipleReceipts && (
          <div className="absolute -left-2 -top-2 z-10 flex h-6 min-w-[24px] items-center justify-center rounded-full bg-accent-500 px-1 text-xs font-bold text-white shadow-md">
            {carouselIndex + 1}/{allReceiptsInStack.length}
          </div>
        )}

        {/* Carousel navigation arrows (card view; list view renders them inline in the meta line) */}
        {hasMultipleReceipts && !isListView && (
          <>
            {prevButton}
            {nextButton}
          </>
        )}

        <ReceiptListItemActionWrapper
          receipt={currentDisplayReceipt}
          viewMode={viewMode}
          meta={listMeta}
          actions={
            <>
              {/* View is always visible — read-only action */}
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onView(currentDisplayReceipt)
                }}
                variant="unstyled"
                data-testid="view-button"
                title={t('receipts.viewReceipt')}
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
              {aiReceiptScanningEnabled && (
                <Gate permission="expenses.create">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      // No funded org can pay — send them to top up instead.
                      if (!canGenerate) {
                        navigate('/billing')
                        return
                      }
                      handleOpenGenerateModal()
                    }}
                    disabled={isAnalyzing}
                    variant="ghost"
                    iconOnly
                    tone={canGenerate ? 'accent' : 'neutral'}
                    data-testid="generate-custom-button"
                    title={t('receipts.generateExpense')}
                  >
                    {isAnalyzing ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent-300 border-t-accent-500" />
                    ) : (
                      <SparklesIcon className="h-4 w-4" />
                    )}
                  </Button>
                </Gate>
              )}
              {completedJobResult && (
                <Gate permission="expenses.create">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate('/expenses/expense', {
                        state: {
                          prefilledData: completedJobResult,
                          fromReceipt: currentDisplayReceipt.id,
                        },
                      })
                    }}
                    variant="ghost"
                    iconOnly
                    tone="primary"
                    data-testid="create-expense-from-analysis-button"
                    title={t('receipts.createExpenseFromAnalysis')}
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </Button>
                </Gate>
              )}
              <Gate permission="receipts.edit">
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsNotesModalOpen(true)
                  }}
                  variant="ghost"
                  iconOnly
                  tone="primary"
                  data-testid="notes-button"
                  title={t('receipts.editNotes')}
                  aria-label={t('receipts.editNotes')}
                >
                  <PencilSquareIcon className="h-4 w-4" />
                </Button>
              </Gate>
              <Gate permission="receipts.create">
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDuplicate()
                  }}
                  disabled={isDuplicating}
                  variant="ghost"
                  iconOnly
                  tone="success"
                  data-testid="duplicate-button"
                  title={t('receipts.duplicateReceipt')}
                >
                  {isDuplicating ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-300 border-t-green-500" />
                  ) : (
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  )}
                </Button>
              </Gate>
              <Gate permission="receipts.edit">
                {onUnlink && hasMultipleReceipts && carouselIndex > 0 && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      // Unlink the currently displayed receipt from the primary
                      onUnlink(receipt.id, currentDisplayReceipt.id)
                      // Reset to primary after unlinking
                      setCarouselIndex(0)
                    }}
                    disabled={isUnlinking}
                    variant="ghost"
                    iconOnly
                    tone="warning"
                    data-testid="unlink-button"
                    title={t('receipts.unlinkReceiptFromStack')}
                  >
                    {isUnlinking ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-orange-300 border-t-orange-500" />
                    ) : (
                      <LinkSlashIcon className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </Gate>
              <Gate permission="receipts.delete">
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete()
                  }}
                  disabled={dialogProps.isPending}
                  variant="ghost"
                  iconOnly
                  tone="error"
                  data-testid="delete-button"
                  title={t('receipts.deleteReceipt')}
                >
                  {dialogProps.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-error-300 border-t-error" />
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                </Button>
              </Gate>
            </>
          }
        />
      </div>
    </>
  )
}
